import { Color, InternalAccessor, Table, TableColumn, Text } from '@billy/design-system'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSkatConnection } from '@features/vatDeclarations/contexts/skatConnectionContext'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { TableIds } from '../../../../../../enums/tableIds'
import { VatDeclarationStatus as VatDeclarationStatusEnum } from '../../../../enums/vatDeclarationStatus'
import { useFetchVatDeclarations } from '../../../../hooks/useFetchVatDeclarations'
import { useVatDeclarationSubmissions } from '../../../../hooks/useVatDeclarationSubmissions'
import { VatDeclarationsTableAccessor } from '../../enums/vatDeclarationsTableAccessor'
import { useVatDeclarationsTableData } from '../../hooks/useVatDeclarationsTableData'
import { VatDeclarationsTableRow } from '../../types/vatDeclarationTableRow'
import { VatDeclarationTableHoverActions } from '../VatDeclarationsTableHoverActions'
import { VatDeclarationsTableStaticActions } from '../VatDeclarationsTableStaticActions'
import { VatDeclarationStatus } from '../VatDeclarationTableStatus'

interface VatDeclarationsTableProps {
  onShowIntegrationForm?: (vatDeclarationId: string) => void
}

export const VatDeclarationsTable = ({ onShowIntegrationForm }: VatDeclarationsTableProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { data: vatDeclarations, isLoading } = useFetchVatDeclarations()
  const { isSkatConnected } = useSkatConnection()

  const vatDeclarationIds = useMemo(
    () => vatDeclarations?.map((declaration) => declaration.id).join(',') || '',
    [vatDeclarations],
  )

  const { submissionsMap, isLoading: isSubmissionsLoading } = useVatDeclarationSubmissions({
    ids: vatDeclarationIds,
  })

  const data = useVatDeclarationsTableData({
    countryId: organization?.countryId,
    currency: organization?.baseCurrencyId,
    isSkatConnected,
    locale: organization?.localeId,
    submissionsMap,
    vatDeclarations: vatDeclarations || [],
  })

  const columns: TableColumn<VatDeclarationsTableRow>[] = useMemo(
    () => [
      {
        accessor: VatDeclarationsTableAccessor.Status,
        alignment: 'center',
        Cell: ({ value: status }) => <VatDeclarationStatus status={status} />,
        size: 'xxxxs',
        sortable: false,
      },
      {
        accessor: VatDeclarationsTableAccessor.PeriodName,
        alignment: 'left',
        Header: t('vat_declarations.table.colum_header.period'),
        size: 'm',
        sortable: false,
      },
      {
        accessor: VatDeclarationsTableAccessor.Description,
        alignment: 'left',
        size: 'l',
        sortable: false,
        Cell: ({ value, row, data }) => {
          const { status } = data[row.id] as VatDeclarationsTableRow
          const textColor =
            status === VatDeclarationStatusEnum.OverduePayment ||
            status === VatDeclarationStatusEnum.OverdueSubmission ||
            status === VatDeclarationStatusEnum.SubmissionFailed
              ? Color.Red700
              : Color.Black

          return (
            <Text color={textColor} variant="bodySM">
              {value}
            </Text>
          )
        },
      },
      {
        accessor: VatDeclarationsTableAccessor.Result,
        alignment: 'right',
        size: 's',
        sortable: false,
      },
      {
        accessor: InternalAccessor.UrlInternal,
      },
      {
        accessor: InternalAccessor.HoverActions,
        Header: '',
        Cell: ({ row, data }) => {
          const { id } = data[row.id] as VatDeclarationsTableRow

          return <VatDeclarationTableHoverActions vatDeclarationId={id} />
        },
        sortable: false,
      },
      {
        accessor: InternalAccessor.StaticActions,
        Header: '',
        Cell: ({ row, data }) => {
          const { currentCorrection, id, periodData, staticActionsItems } = data[row.id] as VatDeclarationsTableRow

          return (
            <VatDeclarationsTableStaticActions
              currentCorrection={currentCorrection}
              items={staticActionsItems}
              periodData={periodData}
              vatDeclarationId={id}
              onShowIntegrationForm={onShowIntegrationForm}
            />
          )
        },
        sortable: false,
      },
    ],
    [onShowIntegrationForm, t],
  )

  return (
    <Table
      columns={columns}
      data={data}
      id={TableIds.VatDeclarations}
      isLoading={isLoading || isSubmissionsLoading}
      withColumnsFiltering={false}
    />
  )
}
